import type { TwoFactorSetup } from '@fluxus/shared';
import { ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ErrorState, LoadingState } from '@/components/states/States';
import { ApiError } from '@/lib/api/errors';
import type { AuthSession } from '@/lib/auth/auth-client';
import { useAuth } from '@/lib/auth/session';
import { RecoveryCodesView, TotpSetupForm } from './two-factor-setup';

type Props = {
  challengeToken: string;
  /** E-mail digitado no login (identifica a conta no app autenticador). */
  email: string;
  /** O desafio venceu ou a pessoa desistiu: volta para e-mail e senha. */
  onRestart: (reason?: unknown) => void;
};

/**
 * Ativação obrigatória durante o login (RF-03): a empresa exige verificação em duas etapas para o
 * perfil e a pessoa ainda não ativou. Ativa aqui; a sessão só abre depois de guardar os códigos.
 */
export function TwoFactorSetupStep({ challengeToken, email, onRestart }: Props) {
  const { client } = useAuth();
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [codes, setCodes] = useState<string[] | null>(null);
  const enter = useRef<(() => Promise<AuthSession>) | null>(null);
  const [entering, setEntering] = useState(false);
  // A função de voltar muda a cada renderização da tela de login: o efeito usa sempre a mais recente.
  const restart = useRef(onRestart);
  restart.current = onRestart;

  useEffect(() => {
    let active = true;
    setLoadError(null);
    client.startLoginTwoFactorSetup(challengeToken).then(
      (s) => active && setSetup(s),
      (err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError && err.code === 'CHALLENGE_EXPIRED') restart.current(err);
        else setLoadError(err);
      },
    );
    return () => {
      active = false;
    };
  }, [client, challengeToken, attempt]);

  if (codes) {
    return (
      <RecoveryCodesView
        codes={codes}
        doneLabel={entering ? 'Entrando…' : 'Entrar no Fluxus'}
        onDone={() => {
          if (entering || !enter.current) return;
          setEntering(true);
          // A sessão muda e a tela de login leva ao destino do perfil.
          enter.current().catch((err: unknown) => {
            setEntering(false);
            onRestart(err);
          });
        }}
      />
    );
  }

  return (
    <>
      <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-yellow-bg text-status-yellow-fg">
        <ShieldAlert className="size-6" aria-hidden />
      </span>
      <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-balance">
        Ative a verificação em duas etapas
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Sua empresa exige a verificação em duas etapas para o seu perfil. Ative agora para entrar: além da
        senha, o Fluxus vai pedir um código do app autenticador do seu celular.
      </p>
      <div className="mt-6">
        {loadError ? (
          <ErrorState
            error={loadError}
            title="Não foi possível começar a ativação"
            onRetry={() => setAttempt((n) => n + 1)}
          />
        ) : setup ? (
          <TotpSetupForm
            setup={setup}
            account={email}
            cancelLabel="Voltar"
            activate={async (code) => {
              try {
                const res = await client.activateLoginTwoFactor(challengeToken, code);
                enter.current = res.enter;
                return res.recoveryCodes;
              } catch (err) {
                // Passou o tempo do desafio: volta para e-mail e senha, com o motivo.
                if (err instanceof ApiError && err.code === 'CHALLENGE_EXPIRED') restart.current(err);
                throw err;
              }
            }}
            onCancel={() => onRestart()}
            onEnabled={setCodes}
          />
        ) : (
          <LoadingState rows={4} label="Preparando a ativação" />
        )}
      </div>
    </>
  );
}
